/**
 * Safe PayNow QR helper.
 *
 * sgqr-code mutates a shared in-memory template. After a generate with no
 * amount (or any call that removes fields), the next generate can throw:
 *   undefined is not an object (evaluating 'arrData[foundIndex].comment')
 *
 * This wrapper clones the template before each generate.
 */
import {
  defaultDataObj,
  updateQrObj,
  getQrValue,
  generateRandomCode,
} from 'sgqr-code/helper/qrHelper';
import { validateParam, removeDataObject } from 'sgqr-code/helper/paramValidation';

function cloneQrTemplate() {
  return JSON.parse(JSON.stringify(defaultDataObj));
}

function validateUenOrPhone(value, type) {
  if (type === 'uen' || type === 'phone') {
    validateParam(value, 16, 'alphanumeric-special');
    return;
  }
  throw new Error('Invalid type for UEN or phone validation');
}

/**
 * @param {Object} options
 * @returns {string} EMVCo PayNow QR payload string
 */
export function getPaynowQRCode({
  initiationMethod = '12',
  referenceNumber = `${generateRandomCode(7)}-${Date.now()}`,
  merchantName = 'NA',
  amount = null,
  amountEditable = '0',
  uen = null,
  phone = null,
  expiryDate = null,
  transactionCurrency = '702',
  countryCode = 'SG',
  merchantCity = 'Singapore',
  uniqueIdentifier = 'SG.PAYNOW',
} = {}) {
  const options = { merchantCategoryCode: '0000' };
  const updateQrData = updateQrObj('01', initiationMethod, cloneQrTemplate());

  if (!['11', '12'].includes(initiationMethod)) {
    throw new Error('Invalid value for initiationMethod');
  }

  validateParam(referenceNumber, 99);
  validateParam(merchantName, 25, 'alphanumeric-special');

  if (!['0', '1'].includes(amountEditable)) {
    throw new Error('Invalid value for amountEditable');
  }

  if (uen) {
    validateUenOrPhone(uen, 'uen');
    updateQrObj('26', '2', updateQrData, '01');
    updateQrObj('26', uen, updateQrData, '02');
  } else if (phone) {
    validateUenOrPhone(phone, 'phone');
    updateQrObj('26', '0', updateQrData, '01');
    updateQrObj('26', phone, updateQrData, '02');
  } else {
    throw new Error('Invalid value for UEN or Phone');
  }

  if (amount !== null && amount !== undefined && amount !== '') {
    validateParam(amount.toString(), 13, 'number');
    updateQrObj('26', amountEditable, updateQrData, '03');
    updateQrObj('54', amount.toString(), updateQrData);
  } else {
    updateQrObj('26', '1', updateQrData, '03');
    removeDataObject(updateQrData, '54');
  }

  validateParam(transactionCurrency, 3, 'number');
  validateParam(countryCode, 2, 'alphanumeric');
  validateParam(merchantCity, 15, 'alphanumeric');
  validateParam(uniqueIdentifier, 9, 'alphanumeric-special');

  updateQrObj('26', uniqueIdentifier, updateQrData, '00');

  if (expiryDate !== null && expiryDate !== undefined) {
    validateParam(expiryDate, 14, 'expiry-date');
    updateQrObj('26', expiryDate, updateQrData, '04');
  } else {
    removeDataObject(updateQrData, '26', '04');
  }

  updateQrObj('52', options.merchantCategoryCode, updateQrData);
  updateQrObj('53', transactionCurrency, updateQrData);
  updateQrObj('58', countryCode, updateQrData);
  updateQrObj('59', merchantName, updateQrData);
  updateQrObj('60', merchantCity, updateQrData);
  updateQrObj('62', referenceNumber, updateQrData, '01');

  return getQrValue(updateQrData);
}
